import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLeagueById, getLeagueHistory } from "../services/leagueService";
import { getErrorMessage } from "../services/api";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import EmptyState from "../components/EmptyState";
import StatPill from "../components/StatPill";
import HistoryGameweekCard from "../components/HistoryGameweekCard";
import AlertMessage from "../components/AlertMessage";
import BackButton from "../components/BackButton";

function LeagueHistoryPage({ userId }) {
  const { leagueId } = useParams();
  const numericLeagueId = Number(leagueId);

  const [league, setLeague] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (!Number.isInteger(numericLeagueId) || numericLeagueId <= 0) {
      setLoading(false);
      setMessage("Invalid league.");
      return;
    }

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setMessage("");

      try {
        const [leagueData, historyData] = await Promise.all([
          getLeagueById(numericLeagueId, { signal: controller.signal }),
          getLeagueHistory(numericLeagueId, userId, { signal: controller.signal }),
        ]);

        setLeague(leagueData || null);
        setHistory(historyData || null);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(error);
        setMessage(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }

    load();

    return () => controller.abort();
  }, [userId, numericLeagueId]);

  const items = Array.isArray(history?.items) ? history.items : [];

  const completedItems = items.filter((item) => item.finished);

  const liveItems = items.filter(
    (item) =>
      !item.finished &&
      ((item.status || "").toLowerCase() === "live" || item.isCurrent)
  );

  const bestWeek =
    items.length > 0
      ? Math.max(
          ...items.map((item) =>
            Number.isInteger(item.currentUserPoints) ? item.currentUserPoints : 0
          )
        )
      : null;

  function formatSubtitle(item) {
    const namePart = item.name || `Gameweek ${item.gameweekNumber}`;
    const seasonPart = item.season || "";
    return seasonPart ? `${namePart} • ${seasonPart}` : namePart;
  }

  if (!userId) {
    return (
      <SectionCard>
        <p className="text-slate-600">Loading user...</p>
      </SectionCard>
    );
  }

  if (!Number.isInteger(numericLeagueId) || numericLeagueId <= 0) {
    return (
      <SectionCard>
        <p className="text-slate-600">Invalid league.</p>
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-9 w-52 animate-pulse rounded-full bg-slate-200" />
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-6 sm:p-8">
          <div className="h-3 w-20 animate-pulse rounded bg-slate-700" />
          <div className="mt-3 h-8 w-56 animate-pulse rounded bg-slate-700" />
          <div className="mt-2 h-4 w-40 animate-pulse rounded bg-slate-700" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl bg-slate-50 px-4 py-4 ring-1 ring-slate-200">
              <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-6 w-24 animate-pulse rounded bg-slate-200" />
            </div>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-3xl bg-white p-4 sm:p-5 shadow-sm ring-1 ring-slate-200">
              <div className="h-5 w-28 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-4 w-40 animate-pulse rounded bg-slate-100" />
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <BackButton to={`/leagues/${numericLeagueId}`} label="Back to League Overview" />

      <PageHeader
        eyebrow="League"
        title={`${league?.name || "League"} History`}
        subtitle="Review past gameweeks and performance."
        dark
        rightContent={
          <div className="rounded-2xl bg-white/10 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-white">
            Invite code:{" "}
            <span className="font-semibold break-all">{league?.inviteCode || "—"}</span>
          </div>
        }
      />

      <AlertMessage message={message} type="error" />

      <section className="grid gap-4 sm:grid-cols-3">
        <StatPill label="Completed Weeks" value={completedItems.length} />
        <StatPill label="Live Weeks" value={liveItems.length} />
        <StatPill label="Best Week" value={bestWeek !== null ? `${bestWeek} pts` : "—"} />
      </section>

      {items.length === 0 ? (
        <EmptyState
          title="No history yet"
          description="Completed gameweeks will appear here."
        />
      ) : (
        <SectionCard title="Gameweek History">
          <div className="grid gap-4 sm:grid-cols-2">
            {items.map((item) => (
              <HistoryGameweekCard
                key={item.gameweekId}
                title={`Gameweek ${item.gameweekNumber}`}
                subtitle={formatSubtitle(item)}
                status={item.status}
                highlight={item.highlight}
                points={
                  item.finished
                    ? `${item.currentUserPoints ?? 0} pts`
                    : item.isCurrent
                    ? "Scoring in progress"
                    : `${item.currentUserPoints ?? 0} pts`
                }
                exactScores={item.currentUserExactScores ?? 0}
                correctResults={item.currentUserCorrectResults ?? 0}
                deadlineUtc={item.deadlineUtc}
                lockUtc={item.lockUtc}
                to={`/leagues/${numericLeagueId}/history/gameweek/${item.gameweekId}`}
              />
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

export default LeagueHistoryPage;
